import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { OrganizationDriverAvailabilitySlot } from "../types/availability";

// ===================
// 🔒 Types
// ===================
type OrgAvailabilitiesArgs = {
  accessToken: string;
  organizationId: string;
};

type UserAvailabilitiesArgs = OrgAvailabilitiesArgs & {
  userId: string;
};

type Recurrence = {
  frequency: "DAILY" | "WEEKLY" | "MONTHLY";
  interval?: number;
  count?: number;
  endDate?: Date;
};

export type AvailabilitySlotInput = {
  accessToken: string;
  organizationId: string;
  userId: string;
  startTime: Date;
  endTime: Date;
  isRecurring?: boolean;
  recurrence?: Recurrence;
};

// ===================
// 🧠 Fetcher Utilities
// ===================

const handleAxiosError = (error: unknown, context: string) => {
  console.error(`❌ Failed to fetch ${context}:`, error);
  if (axios.isAxiosError(error) && error.response) {
    console.error("Status:", error.response.status);
    console.error("Data:", error.response.data);
    console.error("URL:", error.config?.url);
  }
};

export const getOrganizationDriverAvailabilities = async ({
  accessToken,
  organizationId,
}: OrgAvailabilitiesArgs): Promise<OrganizationDriverAvailabilitySlot[]> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/admin/driver-availability/${organizationId}`;
    const { data } = await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    handleAxiosError(error, "organization availabilities");
    return [];
  }
};

export const getUserDriverAvailabilities = async ({
  accessToken,
  organizationId,
  userId,
}: UserAvailabilitiesArgs): Promise<OrganizationDriverAvailabilitySlot[]> => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/admin/driver-availability/${organizationId}/${userId}`;
    const { data } = await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    return data;
  } catch (error) {
    handleAxiosError(error, "user availabilities");
    return [];
  }
};

// ===================
// 🧩 Hooks
// ===================

export const useOrganizationDriverAvailabilities = ({
  accessToken,
  organizationId,
}: OrgAvailabilitiesArgs) =>
  useQuery({
    queryKey: ["org-availabilities", organizationId],
    queryFn: () =>
      getOrganizationDriverAvailabilities({ accessToken, organizationId }),
    enabled: Boolean(accessToken && organizationId),
  });

export const useUserDriverAvailabilities = ({
  accessToken,
  organizationId,
  userId,
}: UserAvailabilitiesArgs) =>
  useQuery({
    queryKey: ["user-availabilities", organizationId, userId],
    queryFn: () =>
      getUserDriverAvailabilities({ accessToken, organizationId, userId }),
    enabled: Boolean(accessToken && organizationId && userId),
  });

export const useCreateAvailabilitySlot = () =>
  useMutation({
    mutationFn: async ({
      accessToken,
      organizationId,
      userId,
      startTime,
      endTime,
      isRecurring,
      recurrence,
    }: AvailabilitySlotInput) => {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/driver-availability/${organizationId}/${userId}`,
        {
          driverId: userId,
          startTime,
          endTime,
          isRecurring,
          recurrence,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
  });

export const useDeleteDriverAvailabilitySlot = () =>
  useMutation({
    mutationFn: async ({
      accessToken,
      organizationDriverAvailabilitySlotId,
    }: {
      accessToken: string;
      organizationDriverAvailabilitySlotId: string;
    }) => {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/admin/driver-availability/${organizationDriverAvailabilitySlotId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return response.data;
    },
  });